import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, BadgePercent } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart, keyOf } from "@/lib/cart";
import { isBulkEligible, BULK_DISCOUNT_MIN_QTY } from "@/lib/beddingConfig";
import { formatPKR } from "@/lib/api";

function resolveImage(url) {
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

function configLine(c) {
  if (!c) return "";
  const parts = [];
  if (c.bedding_type) parts.push(c.bedding_type);
  if (c.size) parts.push(c.size);
  if (c.pc_count) parts.push(`${c.pc_count} pc Set`);
  if (c.pillow_count) parts.push(`${c.pillow_count} Pillows`);
  if (c.curtain_size) parts.push(c.curtain_size);
  return parts.join(" · ");
}

export default function CartDrawer() {
  const { items, drawerOpen, setDrawerOpen, updateQty, removeItem, subtotal, discount, discountQualifiesAny, discountShortfall, hasAnySheets } = useCart();
  const navigate = useNavigate();
  const total = subtotal - discount;

  useEffect(() => {
    document.body.classList.toggle("no-scroll", drawerOpen);
    return () => document.body.classList.remove("no-scroll");
  }, [drawerOpen]);

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
            data-testid="cart-drawer-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: "easeOut" }}
            className="fixed top-0 right-0 z-50 h-dvh w-full sm:w-[440px] bg-cream flex flex-col shadow-2xl"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-5 sm:p-6 border-b border-border-subtle">
              <div>
                <div className="label-eyebrow !text-[10px]">Your Bag</div>
                <h2 className="font-display text-2xl text-ink">{items.length} {items.length === 1 ? "item" : "items"}</h2>
              </div>
              <button onClick={() => setDrawerOpen(false)} className="p-2 -mr-2 text-ink hover:text-sage tap-target" data-testid="cart-close" aria-label="Close bag">
                <X size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain p-5 sm:p-6">
              {items.length === 0 ? (
                <div className="text-center py-12">
                  <div className="font-display text-2xl text-ink mb-2">Your bag is empty</div>
                  <p className="text-ink-soft text-sm mb-6">Soft luxury awaits.</p>
                  <Link
                    to="/"
                    onClick={() => setDrawerOpen(false)}
                    className="inline-block bg-sage hover:bg-sage-dark text-cream px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold transition-colors"
                    data-testid="cart-empty-shop"
                  >
                    Discover the Collection
                  </Link>
                </div>
              ) : (
                <>
                  {discountQualifiesAny && (
                    <div className="mb-5 bg-sage/10 border border-sage/40 px-3 py-2.5 text-xs text-ink flex items-start gap-2" data-testid="cart-discount-banner">
                      <BadgePercent size={14} className="text-sage mt-0.5 flex-shrink-0" />
                      <span><span className="font-semibold text-sage-dark">5% discount applied</span> — on every Bedsheet / Fitted Sheet in your cart.</span>
                    </div>
                  )}
                  {!discountQualifiesAny && hasAnySheets && discountShortfall > 0 && (
                    <div className="mb-5 bg-cream-warm border border-border-subtle px-3 py-2.5 text-xs text-ink-soft flex items-start gap-2" data-testid="cart-discount-nudge">
                      <BadgePercent size={14} className="text-sage mt-0.5 flex-shrink-0" />
                      <span>Add {discountShortfall} more Bedsheet {discountShortfall === 1 ? "or Fitted Sheet" : "/ Fitted Sheet"} to your cart to unlock a <span className="font-semibold text-sage-dark">5% discount</span>. (Threshold: {BULK_DISCOUNT_MIN_QTY} sheets)</span>
                    </div>
                  )}
                  <ul className="space-y-6">
                  {items.map((it) => {
                    const k = keyOf(it);
                    return (
                      <li key={k} className="flex gap-4" data-testid={`cart-item-${it.product_id}`}>
                        <div className="w-20 h-24 bg-cream-warm flex-shrink-0 overflow-hidden">
                          {it.image ? (
                            <img src={resolveImage(it.image)} alt={it.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="h-full flex items-center justify-center font-display text-xs text-sage">BL</div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display text-lg text-ink leading-tight">{it.name}</div>
                          {configLine(it.config) && (
                            <div className="text-xs text-ink-soft mt-0.5">{configLine(it.config)}</div>
                          )}
                          <div className="mt-2 flex items-center justify-between">
                            <div className="flex items-center border border-border-subtle">
                              <button
                                onClick={() => updateQty(k, it.quantity - 1)}
                                className="min-w-[42px] min-h-[42px] flex items-center justify-center text-ink-soft hover:text-ink"
                                data-testid={`cart-dec-${it.product_id}`}
                                aria-label="Decrease quantity"
                              >
                                <Minus size={14} />
                              </button>
                              <span className="px-2 text-sm">{it.quantity}</span>
                              <button
                                onClick={() => updateQty(k, it.quantity + 1)}
                                className="min-w-[42px] min-h-[42px] flex items-center justify-center text-ink-soft hover:text-ink"
                                data-testid={`cart-inc-${it.product_id}`}
                                aria-label="Increase quantity"
                              >
                                <Plus size={14} />
                              </button>
                            </div>
                            <div className="text-sm font-semibold text-ink">{formatPKR(it.price * it.quantity)}</div>
                          </div>
                          {isBulkEligible(it, items) && (
                            <div className="mt-1 text-[10px] uppercase tracking-widest text-sage-dark font-semibold" data-testid={`cart-line-discount-${it.product_id}`}>
                              5% off applied
                            </div>
                          )}
                          <button
                            onClick={() => removeItem(k)}
                            className="mt-2 min-h-[40px] text-xs text-ink-soft hover:text-rose-dark inline-flex items-center gap-1"
                            data-testid={`cart-remove-${it.product_id}`}
                          >
                            <Trash2 size={12} /> Remove
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                </>
              )}
            </div>

            {items.length > 0 && (
              <div className="p-5 sm:p-6 pb-[max(1.25rem,env(safe-area-inset-bottom))] border-t border-border-subtle bg-cream-warm">
                <div className="space-y-1.5 mb-4">
                  <div className="flex items-center justify-between text-sm text-ink-soft">
                    <span>Subtotal</span>
                    <span data-testid="cart-subtotal">{formatPKR(subtotal)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex items-center justify-between text-sm text-sage-dark font-semibold" data-testid="cart-discount-line">
                      <span>Bulk discount (5%)</span>
                      <span>− {formatPKR(discount)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-ink/10">
                    <span className="label-eyebrow !text-[10px]">Total</span>
                    <span className="font-display text-2xl text-ink" data-testid="cart-total">{formatPKR(total)}</span>
                  </div>
                </div>
                <p className="text-xs text-ink-soft mb-4">Shipping calculated at checkout. Cash on delivery available across Pakistan.</p>
                <button
                  onClick={() => {
                    setDrawerOpen(false);
                    navigate("/checkout");
                  }}
                  className="w-full bg-sage hover:bg-sage-dark text-cream py-4 text-xs uppercase tracking-[0.22em] font-semibold transition-colors"
                  data-testid="cart-checkout-cta"
                >
                  Proceed to Checkout
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
